import { useUser } from "@clerk/clerk-react";
import {
  AutoStoriesOutlined,
  CampaignOutlined,
  Check,
  VolunteerActivismOutlined,
} from "@mui/icons-material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import { useEffect, useState } from "react";
import { ImpactCampaignPickerDialog } from "@/components/impactReport/ImpactCampaignPickerDialog";
import { ImpactFilterMenu } from "@/components/impactReport/ImpactFilterMenu";
import { ImpactFilterPills } from "@/components/impactReport/ImpactFilterPills";
import { ImpactPeriodMenu } from "@/components/impactReport/ImpactPeriodMenu";
import { ImpactReportDownloadControl } from "@/components/impactReport/ImpactReportDownloadControl";
import {
  IMPACT_REPORT_ALL_CAMPAIGNS_LABEL,
  IMPACT_REPORT_BAR_NARROW_MAX_PX,
  IMPACT_REPORT_CAUSE_MENU_EMPTY_TEXT,
  IMPACT_REPORT_CONTROL_BAR_ARIA_LABEL,
  IMPACT_REPORT_CONTROL_LABELS,
  IMPACT_REPORT_PDF_EXPORT_ENABLED,
  IMPACT_REPORT_VIEW_MODE_ENTRIES,
  IMPACT_REPORT_VIEW_MODE_LABELS,
} from "@/constants/impactReport";
import { ImpactMenuCoordinatorProvider } from "@/context/ImpactMenuCoordinatorContext";
import { useImpactReportContext } from "@/context/ImpactReportContext";
import { useImpactReport } from "@/hooks/useImpactReport";
import type { CauseCategory } from "@/interfaces/impactReport";
import {
  readImpactCampaignRecentsCollapsed,
  readRecentImpactCampaignIds,
  rememberImpactCampaignRecentsCollapsed,
  rememberRecentImpactCampaignIds,
} from "@/utils/impact-campaign-recents-helper";
import { isClientImpactReportDeferred } from "@/utils/impact-report-helper";

/** Long lists scroll inside the menu instead of running off the bottom of the viewport. */
const LIST_MENU_MAX_HEIGHT = 360;
const LIST_MENU_MIN_WIDTH = 320;

function recentCampaignsStorage(): Storage | undefined {
  return typeof window === "undefined" ? undefined : window.localStorage;
}

/** Recents are per Clerk user; with no user there is nobody to remember them for. */
function readRecentCampaignIds(userId: string | undefined): string[] {
  return userId === undefined ? [] : readRecentImpactCampaignIds(recentCampaignsStorage(), userId);
}

function readRecentCampaignsCollapsed(userId: string | undefined): boolean {
  return (
    userId !== undefined && readImpactCampaignRecentsCollapsed(recentCampaignsStorage(), userId)
  );
}

/**
 * The Impact route's app-bar contents: active-filter pills on the left and the controls on the
 * right — VIEW, CAUSE, CAMPAIGN, DATE, and, behind `IMPACT_REPORT_PDF_EXPORT_ENABLED` and in the
 * hero view only, DOWNLOAD.
 *
 * Rendered by `Drawer.tsx` INSIDE the standard `Toolbar`, at the standard 64px bar height. It is a
 * content swap, not a second bar and not a layout change: every other route's app bar is untouched.
 *
 * ── THE REFETCH BOUNDARY ──────────────────────────────────────────────────────────────────────
 * VIEW is local and free; CAUSE, CAMPAIGN and DATE are real `ReportFilter` fields and each change
 * costs one request. That difference is the point of the whole slice, so it is worth stating where
 * the controls sit side by side: `setViewMode` writes a value the composer has no parameter for,
 * so it cannot reach a query key, and nothing in this file invalidates, refetches or resets a
 * query — `useQueryClient` is not imported here.
 *
 * ── THE SINGLE-OPEN MENU COORDINATOR ─────────────────────────────────────────────────────────
 * `ImpactMenuCoordinatorProvider` is mounted here, around the whole return value, because this is
 * the one component that is an ancestor of every `ImpactFilterMenu` instance on the bar: the four
 * controls below AND the collapsed active-filters pill rendered inside `ImpactFilterPills` (its
 * own menu, for when several filters collapse into one pill — see that file). Without a shared
 * ancestor for all five, two of them coordinating and the third not would just move the double-tap
 * bug rather than fix it. See `ImpactFilterMenu`'s own file comment for what the coordination
 * actually does.
 *
 * CAMPAIGN is the exception in shape, not in coordination: its trigger opens the campaign picker
 * DIALOG rather than a menu, so it owns no menu of its own. It still carries the trigger marker
 * the backdrops forward clicks to, and clicking it still closes whichever menu is open.
 */
export function ImpactControlBar() {
  const {
    controls,
    setControls,
    viewMode,
    setViewMode,
    filter,
    canRequestReport,
    causeOptions,
    refreshCauseOptions,
    campaignMenuTriggerRef,
    openCampaignMenu,
    causeMenuTriggerRef,
  } = useImpactReportContext();

  // The SAME memoised filter and the SAME `enabled` the page passes, so react-query hashes one key
  // and this call reads the page's cache entry rather than running the aggregate a second time.
  // The bar needs the payload for one reason only: the CAUSE options are `causeData`'s own
  // `{ causeValue, causeName }` pairs, and there is nowhere else to read them from.
  const { data } = useImpactReport(filter, canRequestReport);

  // Requestable with no campaign chosen is exactly the org-wide report; GFG staff never get here.
  const isOrgWideReport = canRequestReport && controls.campaignIds.length === 0;

  useEffect(() => {
    // The provider ignores this while a cause filter is applied — see `refreshCauseOptions`, where
    // the narrowing problem is spelled out. Offered on every settled payload regardless, so the
    // decision lives in exactly one place.
    refreshCauseOptions(isClientImpactReportDeferred(data) ? undefined : data?.data.causeData);
  }, [data, refreshCauseOptions]);

  const [isCampaignPickerOpen, setIsCampaignPickerOpen] = useState(false);

  const { user } = useUser();
  const userId = user?.id;
  const [recentsUserId, setRecentsUserId] = useState(userId);
  const [recentCampaignIds, setRecentCampaignIds] = useState(() => readRecentCampaignIds(userId));
  const [recentCollapsed, setRecentCollapsed] = useState(() =>
    readRecentCampaignsCollapsed(userId),
  );
  if (userId !== recentsUserId) {
    setRecentsUserId(userId);
    setRecentCampaignIds(readRecentCampaignIds(userId));
    setRecentCollapsed(readRecentCampaignsCollapsed(userId));
  }

  function changeRecentCollapsed(collapsed: boolean) {
    setRecentCollapsed(collapsed);
    if (userId !== undefined) {
      rememberImpactCampaignRecentsCollapsed(recentCampaignsStorage(), userId, collapsed);
    }
  }

  function applyCampaignSelection(campaignIds: string[]) {
    // The picker's whole answer replaces the selection, and an empty answer returns the page to its
    // pre-selection prompt, which issues no request — never an unscoped one.
    setControls((previous) => ({ ...previous, campaignIds }));
    if (userId !== undefined && campaignIds.length > 0) {
      setRecentCampaignIds(
        rememberRecentImpactCampaignIds(recentCampaignsStorage(), userId, campaignIds),
      );
    }
    setIsCampaignPickerOpen(false);
  }

  function toggleCause(causeValue: CauseCategory) {
    setControls((previous) => ({
      ...previous,
      // The stored value travels verbatim — matched byte-for-byte upstream, never re-cased,
      // trimmed or slugified. The rendered `causeName` is a label and never leaves the screen.
      cause: previous.cause.includes(causeValue)
        ? previous.cause.filter((value) => value !== causeValue)
        : [...previous.cause, causeValue],
    }));
  }

  return (
    <ImpactMenuCoordinatorProvider>
      <Box
        component="section"
        aria-label={IMPACT_REPORT_CONTROL_BAR_ARIA_LABEL}
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
        }}
      >
        <ImpactFilterPills onOpenCampaignMenu={openCampaignMenu} />

        {/* NARROW WIDTHS SCROLL, THEY DO NOT CLIP. The four controls are ~500px of non-shrinkable
          buttons; the bar's host is `position: fixed`, and a fixed element contributes nothing to
          the document's scrollable overflow — so anything past the viewport edge would be
          unreachable rather than merely off-screen, and CAMPAIGN (the only control that can
          produce a scope) is the last-but-one of the four. Scrolling this group horizontally keeps
          every control operable at 375px while the bar's own geometry stays exactly 64px:
          `overflowY: hidden` (forced to a non-visible value by `overflowX: auto` anyway) plus the
          4px padding/negative-margin pair so a 44px control's 2px focus ring is not shaved off.
          Keyboard reach is unaffected — Tab scrolls a focused control into view. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            // Tighter at the 320px floor: three gaps between four buttons, so 12px -> 8px buys back
            // 12px of bar — the last few pixels needed once the buttons themselves have narrowed to
            // 40px. Keyed to the same threshold `ImpactFilterMenu` narrows the buttons at, so the
            // two adjustments move together.
            [`@media (max-width: ${IMPACT_REPORT_BAR_NARROW_MAX_PX}px)`]: { gap: "8px" },
            // NEVER SHRINK. The pills row and this group are siblings that can both absorb a
            // shortfall, and each button inside is itself `flexShrink: 0` — so letting this group
            // shrink pushes whole controls into an overflow whose scrollbar is hidden, and they
            // vanish with no affordance at all. Measured at 1440px with 3 causes + 3 campaigns:
            // DATE was clipped mid-word and clear-all was reduced to "C". The pills are removable
            // and secondary; these four are the only way to operate the report, and CAMPAIGN is the
            // only one that can produce a scope. So the pills yield and scroll instead.
            flexShrink: 0,
            minWidth: 0,
            overflowX: "auto",
            overflowY: "hidden",
            p: "4px",
            m: "-4px",
            // The scrollbar is suppressed rather than allowed to eat the bar's height; the controls
            // remain reachable by touch drag, trackpad, and focus.
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {/* ── VIEW — local, single-select, zero network ── */}
          <ImpactFilterMenu
            label={IMPACT_REPORT_CONTROL_LABELS.view}
            icon={<AutoStoriesOutlined />}
            // Never lit: VIEW always has a value, so an "active" VIEW would mean nothing.
            isActive={false}
            buttonAriaLabel={`View: ${IMPACT_REPORT_VIEW_MODE_LABELS[viewMode]}`}
          >
            {(closeMenu) =>
              IMPACT_REPORT_VIEW_MODE_ENTRIES.map(([mode, label]) => (
                <MenuItem
                  key={mode}
                  selected={viewMode === mode}
                  onClick={() => {
                    // The whole point of the contract: this changes what is DRAWN, never what is
                    // asked. No invalidation, no refetch, no query key — both statement registers
                    // are already in the payload the page is holding.
                    setViewMode(mode);
                    closeMenu();
                  }}
                  sx={{ fontSize: 13.5, gap: 1.25 }}
                >
                  <Box component="span" sx={{ flex: 1 }}>
                    {label}
                  </Box>
                  <Check
                    sx={{
                      fontSize: 16,
                      color: viewMode === mode ? "var(--color-dark-dust)" : "transparent",
                    }}
                  />
                </MenuItem>
              ))
            }
          </ImpactFilterMenu>

          {/* ── CAUSE — multi-select. `cause` is a `CauseCategory[]` on the contract; the
            prototype's single-select is a prototype limitation, not the contract's shape. ── */}
          <ImpactFilterMenu
            label={IMPACT_REPORT_CONTROL_LABELS.cause}
            icon={<VolunteerActivismOutlined />}
            isActive={controls.cause.length > 0}
            buttonAriaLabel="Filter by cause"
            menuMinWidth={LIST_MENU_MIN_WIDTH}
            menuMaxHeight={LIST_MENU_MAX_HEIGHT}
            // The empty state's "clearCause" action focuses this button directly — see
            // `causeMenuTriggerRef` on the context for why.
            triggerButtonRef={causeMenuTriggerRef}
          >
            {() =>
              causeOptions.length === 0
                ? [
                    <MenuItem key="empty" disabled sx={{ fontSize: 13, whiteSpace: "normal" }}>
                      {IMPACT_REPORT_CAUSE_MENU_EMPTY_TEXT}
                    </MenuItem>,
                  ]
                : causeOptions.map((option) => (
                    <MenuItem
                      key={option.causeValue}
                      // No `closeMenu`: the menu stays open so several causes can be bunched onto
                      // one report instead of costing a request each.
                      onClick={() => toggleCause(option.causeValue)}
                      sx={{ fontSize: 13.5, gap: 0.5 }}
                    >
                      <Checkbox
                        checked={controls.cause.includes(option.causeValue)}
                        size="small"
                        sx={{ p: 0.5, mr: 1 }}
                      />
                      {/* The authored label is shown; `causeValue` is what is sent. Neither is ever
                        derived from the other. */}
                      {option.causeName}
                    </MenuItem>
                  ))
            }
          </ImpactFilterMenu>

          {/* ── CAMPAIGN — opens the campaign picker dialog, and the ONLY control that produces a
            scope. Apply replaces `campaignIds` with the dialog's selection; any other close
            discards it. ── */}
          <ImpactFilterMenu
            label={
              isOrgWideReport
                ? IMPACT_REPORT_ALL_CAMPAIGNS_LABEL
                : IMPACT_REPORT_CONTROL_LABELS.campaign
            }
            icon={<CampaignOutlined />}
            // The megaphone sits on a diagonal with empty corners — see `iconOpticalScale`.
            iconOpticalScale={1.18}
            isActive={controls.campaignIds.length > 0}
            buttonAriaLabel={
              isOrgWideReport
                ? `Choose campaigns: ${IMPACT_REPORT_ALL_CAMPAIGNS_LABEL}`
                : "Choose campaigns"
            }
            // `openCampaignMenu` on the context clicks this, so the pills and the empty state
            // open the dialog through the same button.
            triggerButtonRef={campaignMenuTriggerRef}
            onTriggerClick={() => setIsCampaignPickerOpen(true)}
          />
          <ImpactCampaignPickerDialog
            open={isCampaignPickerOpen}
            selectedCampaignIds={[...controls.campaignIds]}
            recentCampaignIds={recentCampaignIds}
            recentCollapsed={recentCollapsed}
            onRecentCollapsedChange={changeRecentCollapsed}
            onApply={applyCampaignSelection}
            onClose={() => setIsCampaignPickerOpen(false)}
            // Not MUI's restore: when `openCampaignMenu` opened the dialog from the empty state or
            // the collapsed pill's panel, that element is gone after Apply and focus would fall to
            // `<body>`. The trigger is always mounted.
            onExited={() => campaignMenuTriggerRef.current?.focus()}
          />

          {/* ── DATE — presets plus a custom range that opens inside the menu ── */}
          <ImpactPeriodMenu />

          {/* ── DOWNLOAD — last, because it fires an action rather than narrowing the report.
            Hero view only; see the component for why it stays mounted in the cards view. ── */}
          {IMPACT_REPORT_PDF_EXPORT_ENABLED && <ImpactReportDownloadControl />}
        </Box>
      </Box>
    </ImpactMenuCoordinatorProvider>
  );
}
